<template>
  <div
    class="min-h-screen bg-white dark:bg-[#0c1427]"
  >
    <!-- Statistics Cards - Moved to top -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
      <div v-if="!loading" class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <!-- Total Contacts Card -->
        <div class="bg-white dark:bg-[#1a2332] rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-gray-800 hover:shadow-xl transition-all duration-500 hover:scale-[1.02] hover:-translate-y-1 animate-fade-in-up" style="animation-delay: 0.1s">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                {{ directT('contacts.totalContacts') }}
              </p>
              <p class="text-3xl font-bold text-gray-900 dark:text-white">
                {{ contacts.length }}
              </p>
            </div>
            <div class="w-12 h-12 rounded-full flex items-center justify-center animate-bounce-in" 
                 style="background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%); animation-delay: 0.3s">
              <i class="material-symbols-outlined !text-[24px] text-white">
                group
              </i>
            </div>
          </div>
        </div>

        <!-- New Contacts in Last 7 Days -->
        <div class="bg-white dark:bg-[#1a2332] rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-gray-800 hover:shadow-xl transition-all duration-500 hover:scale-[1.02] hover:-translate-y-1 animate-fade-in-up" style="animation-delay: 0.2s">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                {{ directT('contacts.newContactsLast7Days') }}
              </p>
              <p class="text-3xl font-bold text-gray-900 dark:text-white">
                {{ newContactsCount }}
              </p>
            </div>
            <div class="w-12 h-12 rounded-full flex items-center justify-center animate-bounce-in" 
                 style="background: linear-gradient(135deg, #10b981 0%, #06b6d4 100%); animation-delay: 0.4s">
              <i class="material-symbols-outlined !text-[24px] text-white">
                person_add
              </i>
            </div>
          </div>
        </div>

        <!-- Status Breakdown -->
        <div class="bg-white dark:bg-[#1a2332] rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-gray-800 hover:shadow-xl transition-all duration-500 hover:scale-[1.02] hover:-translate-y-1 animate-fade-in-up" style="animation-delay: 0.3s">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                {{ directT('contacts.statusBreakdown') }}
              </p>
              <div class="space-y-2 mt-2">
                <div class="flex items-center gap-4">
                  <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ directT('contacts.active') }}:</span>
                  <span class="text-lg font-bold text-green-600 dark:text-green-400">{{ activeContactsCount }}</span>
                </div>
                <div class="flex items-center gap-4">
                  <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ directT('contacts.deactive') }}:</span>
                  <span class="text-lg font-bold text-gray-500 dark:text-gray-400">{{ inactiveContactsCount }}</span>
                </div>
              </div>
            </div>
            <div class="w-12 h-12 rounded-full flex items-center justify-center animate-bounce-in" 
                 style="background: linear-gradient(135deg, #f59e0b 0%, #ec4899 100%); animation-delay: 0.5s">
              <i class="material-symbols-outlined !text-[24px] text-white">
                pie_chart
              </i>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Header Section with Sticky Navigation -->
    <div 
      class="sticky top-0 z-[5] backdrop-blur-xl bg-white/70 dark:bg-[#0c1427]/70 animate-fade-in-down"
    >
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div class="flex flex-col-reverse sm:flex-row items-center justify-between gap-4">
          <!-- Search Bar with Modern Design -->
          <div class="w-full sm:w-auto">
            <div class="relative group">
          <label
                class="absolute ltr:left-4 rtl:right-4 top-1/2 -translate-y-1/2 transition-all duration-300 pointer-events-none"
                :class="searchQuery ? 'opacity-0 ltr:-translate-x-2 rtl:translate-x-2' : 'opacity-100'"
          >
                <i class="material-symbols-outlined !text-[22px]" style="color: #9ca3af;">search</i>
          </label>
          <input
            type="text"
            v-model="searchQuery"
                :placeholder="directT('contacts.searchContact')"
                class="w-full sm:w-[380px] h-[48px] ltr:pl-12 ltr:pr-12 rtl:pr-12 rtl:pl-12 rounded-full border-2 transition-all duration-300 text-sm sm:text-base font-medium shadow-md hover:shadow-lg focus:shadow-xl outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500 bg-white dark:bg-[#1a2332] text-gray-900 dark:text-white border-gray-200 dark:border-[#2a3441] focus:border-purple-500 dark:focus:border-purple-400"
              />
              <div 
                v-if="searchQuery"
                class="absolute ltr:right-3 rtl:left-3 top-1/2 -translate-y-1/2 cursor-pointer transition-all duration-200 hover:scale-110"
                @click="searchQuery = ''"
              >
                <div 
                  class="w-7 h-7 rounded-full flex items-center justify-center transition-colors duration-200"
                  style="background-color: rgba(139, 92, 246, 0.1);"
                  @mouseover="$event.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.2)'"
                  @mouseleave="$event.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.1)'"
                >
                  <i class="material-symbols-outlined !text-[18px]" style="color: #8b5cf6;">close</i>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Add Contact Button -->
          <button
            type="button"
            class="w-full sm:w-auto group flex items-center justify-center gap-2 px-4 sm:px-6 py-3 rounded-full font-medium text-sm sm:text-base transition-all duration-300 transform hover:scale-105 hover:shadow-xl shadow-lg"
            style="background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%); color: #ffffff;"
            @click="openAddContactModal"
          >
            <div 
              class="w-8 h-8 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:rotate-180"
              style="background-color: rgba(255, 255, 255, 0.2);"
            >
              <i class="material-symbols-outlined !text-[20px]" style="color: #ffffff;">
                add
              </i>
            </div>
            <span class="font-semibold">{{ directT('contacts.addNewContact') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content Container -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Loading State -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-20">
        <div class="relative">
          <div class="w-16 h-16 rounded-full border-4 animate-spin" 
               style="border-color: #e5e7eb; border-top-color: #8b5cf6;">
          </div>
        </div>
        <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">{{ directT('common.loading') }}</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredContacts.length === 0" class="text-center py-20">
        <div class="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4" 
             style="background-color: #f3f4f6;">
          <i class="material-symbols-outlined !text-[40px]" style="color: #9ca3af;">person_off</i>
        </div>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {{ searchQuery ? directT('contacts.noContactsFound') : directT('contacts.noContactsFound') }}
        </h3>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          {{ searchQuery ? directT('contacts.tryAdjustingSearch') : directT('contacts.addFirstContact') }}
        </p>
      </div>

      <!-- Contacts Table with Modern Design -->
      <div v-else class="overflow-hidden rounded-3xl shadow-xl animate-fade-in-up bg-white dark:bg-[#1a2332] border border-gray-200 dark:border-gray-700" 
           style="animation-delay: 0.6s">
        <div class="overflow-x-auto custom-scrollbar mobile-table-scroll">
          <table class="w-full mobile-table" :dir="isRTL ? 'rtl' : 'ltr'">
            <thead>
              <tr style="background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);">
                <th class="sticky top-0 z-10 px-6 py-4 text-center font-semibold text-xs uppercase tracking-wider text-white">
                  {{ directT('contacts.name') }}
              </th>
                <th class="sticky top-0 z-10 px-6 py-4 text-center font-semibold text-xs uppercase tracking-wider text-white">
                  {{ directT('contacts.phone') }}
              </th>
                <th class="sticky top-0 z-10 px-6 py-4 text-center font-semibold text-xs uppercase tracking-wider text-white">
                  {{ directT('contacts.status') }}
              </th>
                <th class="sticky top-0 z-10 px-6 py-4 text-center font-semibold text-xs uppercase tracking-wider text-white">
                  {{ directT('contacts.action') }}
              </th>
            </tr>
          </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-600">
              <tr v-for="contact in filteredContacts" 
                  :key="contact.id"
                  class="group hover:bg-gray-50 dark:hover:bg-[#253344] transition-all duration-200 bg-white dark:bg-[#1a2332]">
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <div class="flex items-center justify-center">
                    <div 
                      v-if="contact.profile_picture_link"
                      class="w-10 h-10 rounded-full overflow-hidden flex-shrink-0"
                      :style="{ marginRight: isRTL ? '0' : '0.75rem', marginLeft: isRTL ? '0.75rem' : '0' }"
                    >
                    <img
                      :src="contact.profile_picture_link"
                        :alt="contact.name"
                        class="w-full h-full object-cover"
                        @error="$event.target.style.display = 'none'; $event.target.nextElementSibling.style.display = 'flex'"
                      />
                      <div 
                        class="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-white"
                        :style="{ backgroundColor: getAvatarColor(contact.name), display: 'none' }"
                      >
                        {{ getInitials(contact.name) }}
                      </div>
                    </div>
                    <div 
                      v-else
                      class="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-white flex-shrink-0"
                      :style="{ backgroundColor: getAvatarColor(contact.name), marginRight: isRTL ? '0' : '0.75rem', marginLeft: isRTL ? '0.75rem' : '0' }"
                    >
                      {{ getInitials(contact.name) }}
                    </div>
                    <div class="min-w-[120px]">
                      <p class="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {{ contact.name }}
                      </p>
                  </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <div class="flex items-center justify-center gap-2 ltr:flex-row rtl:flex-row-reverse">
                    <i class="material-symbols-outlined !text-[16px] text-gray-400 dark:text-gray-500">phone</i>
                    <span class="text-sm text-gray-700 dark:text-gray-300">
                      {{ contact.phone || directT('common.notAvailable') }}
                    </span>
                </div>
              </td>
                <td class="px-6 py-4 whitespace-nowrap text-center">
                <span
                    class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                    :style="getStatusStyle(contact.status)"
                >
                    {{ contact.status === 'active' ? directT('contacts.active') : directT('contacts.deactive') }}
                </span>
              </td>
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <div class="flex items-center justify-center gap-2 ltr:flex-row rtl:flex-row-reverse">
                  <button
                    type="button"
                      class="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
                      style="background-color: #ede9fe;"
                    @click="openViewPopup(contact)"
                      @mouseover="$event.currentTarget.style.backgroundColor = '#ddd6fe'"
                      @mouseleave="$event.currentTarget.style.backgroundColor = '#ede9fe'"
                  >
                      <i class="material-symbols-outlined !text-[18px]" style="color: #7c3aed;">
                      visibility
                    </i>
                  </button>
                  <button
                    type="button"
                      class="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
                      style="background-color: #fee2e2;"
                      @click="deleteContact(contact)"
                      @mouseover="$event.currentTarget.style.backgroundColor = '#fecaca'"
                      @mouseleave="$event.currentTarget.style.backgroundColor = '#fee2e2'"
                    >
                      <i class="material-symbols-outlined !text-[18px]" style="color: #dc2626;">
                        delete
                      </i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      </div>
    </div>

    <!-- View Contact Popup -->
    <div
      :class="[
        'view-contact-popup view-contact-modal fixed transition-all inset-0 z-[10000]',
        { active: showViewPopup },
        isMobile ? 'top-[80px]' : 'overflow-x-hidden overflow-y-auto p-4'
      ]"
      v-if="showViewPopup && selectedContact"
    >
      <!-- Backdrop with blur -->
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="closeViewPopup"></div>
      
      <!-- Modal Content -->
      <div
        :class="isMobile ? 'relative w-full h-full' : 'popup-dialog flex transition-all max-w-[550px] min-h-full items-center mx-auto relative'"
      >
        <div
          :class="isMobile ? 'modal-content w-full h-full flex flex-col rounded-3xl bg-white dark:bg-[#1c1c1e] overflow-hidden' : 'modal-content w-full bg-white dark:bg-[#1c1c1e] rounded-3xl shadow-2xl overflow-hidden transform transition-all duration-300 scale-100 opacity-100'"
        >
          <!-- Modal Header with gradient -->
          <div 
            class="relative px-6 py-5"
            :class="isMobile ? 'rounded-t-3xl' : ''"
            style="background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);"
          >
            <div class="flex items-center justify-between" style="align-items: center;">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <i class="material-symbols-outlined !text-[24px] text-white">
                    person
                  </i>
                </div>
                <h2 class="text-xl font-bold text-white" style="margin: 0;">
                  {{ directT('contacts.viewContact') }}
                </h2>
              </div>
              <button
                @click="closeViewPopup"
                class="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 transition-all duration-200 flex items-center justify-center group"
                style="border: none; cursor: pointer;"
              >
                <i class="material-symbols-outlined !text-[20px] text-white group-hover:rotate-90 transition-transform duration-200">
                  close
                </i>
              </button>
            </div>
          </div>
          
          <!-- Modal Body -->
          <div class="p-6 space-y-6 custom-scrollbar"
               :class="isMobile ? 'flex-1 overflow-y-auto' : 'max-h-[60vh] overflow-y-auto'">
            <!-- Contact Avatar and Name -->
            <div class="flex items-center gap-4 pb-6 border-b border-gray-200 dark:border-gray-600">
              <div 
                v-if="selectedContact?.profile_picture_link"
                class="w-20 h-20 rounded-2xl overflow-hidden shadow-lg"
              >
                <img 
                  :src="selectedContact.profile_picture_link" 
                  :alt="selectedContact.name"
                  class="w-full h-full object-cover"
                  @error="$event.target.style.display = 'none'; $event.target.nextElementSibling.style.display = 'flex'"
                />
                <div 
                  class="w-20 h-20 rounded-2xl flex items-center justify-center font-bold text-white text-2xl shadow-lg"
                  :style="{ backgroundColor: getAvatarColor(selectedContact?.name || ''), display: 'none' }"
                >
                  {{ getInitials(selectedContact?.name || '') }}
                </div>
              </div>
              <div 
                v-else
                class="w-20 h-20 rounded-2xl flex items-center justify-center font-bold text-white text-2xl shadow-lg"
                :style="{ backgroundColor: getAvatarColor(selectedContact?.name || '') }"
              >
                {{ getInitials(selectedContact?.name || '') }}
              </div>
              <div>
                <h3 class="text-2xl font-bold text-gray-900 dark:text-white">
                  {{ selectedContact?.name || directT('common.notAvailable') }}
                </h3>
              </div>
            </div>
            
            <!-- Contact Information Grid -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <!-- Phone -->
              <div class="rounded-xl p-4 bg-gray-50 dark:bg-gray-700">
                <div class="flex items-center gap-2 mb-2">
                  <i class="material-symbols-outlined !text-[18px] text-purple-600 dark:text-purple-400">phone</i>
                  <span class="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                    {{ directT('contacts.phone') }}
                  </span>
                </div>
                <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {{ selectedContact?.phone || directT('common.notAvailable') }}
                </p>
              </div>
              
              <!-- Company -->
              <div class="rounded-xl p-4 bg-gray-50 dark:bg-gray-700">
                <div class="flex items-center gap-2 mb-2">
                  <i class="material-symbols-outlined !text-[18px] text-purple-600 dark:text-purple-400">business</i>
                  <span class="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                    {{ directT('contacts.company') }}
                  </span>
                </div>
                <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {{ selectedContact?.company || directT('common.notAvailable') }}
                </p>
              </div>
              
              <!-- Status -->
              <div class="rounded-xl p-4 bg-gray-50 dark:bg-gray-700">
                <div class="flex items-center gap-2 mb-2">
                  <i class="material-symbols-outlined !text-[18px] text-purple-600 dark:text-purple-400">toggle_on</i>
                  <span class="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                    {{ directT('contacts.status') }}
                  </span>
                </div>
                <span 
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                  :style="getStatusStyle(selectedContact?.status || 'inactive')"
                >
                  {{ selectedContact?.status === 'active' ? directT('contacts.active') : directT('contacts.deactive') }}
                </span>
              </div>
            </div>
            
            <!-- Additional Information -->
            <div class="space-y-4">
              <!-- Created Date -->
              <div class="rounded-xl p-4 bg-gray-50 dark:bg-gray-700">
                <div class="flex items-center gap-2 mb-2">
                  <i class="material-symbols-outlined !text-[18px] text-purple-600 dark:text-purple-400">calendar_today</i>
                  <span class="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                    {{ directT('contacts.created') }}
                  </span>
                </div>
                <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {{ formatDate(selectedContact?.created_at) }}
                </p>
              </div>
              
              <!-- Last Contact -->
              <div class="rounded-xl p-4 bg-gray-50 dark:bg-gray-700">
                <div class="flex items-center gap-2 mb-2">
                  <i class="material-symbols-outlined !text-[18px] text-purple-600 dark:text-purple-400">schedule</i>
                  <span class="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                    {{ directT('contacts.lastContact') }}
                  </span>
                </div>
                <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {{ formatDate(selectedContact?.last_contact) }}
                </p>
              </div>
            </div>
          </div>
          
          <!-- Modal Footer -->
          <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 flex items-center justify-between">
            <button
              type="button"
              @click="() => { openEditModal(selectedContact); closeViewPopup(); }"
              class="px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 hover:shadow-lg"
              style="background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); color: #ffffff;"
            >
              <i class="material-symbols-outlined !text-[16px] align-middle mr-2">edit</i>
              {{ directT('contacts.edit') }}
            </button>
            <button
              type="button"
              @click="closeViewPopup"
              class="px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 hover:shadow-md"
              style="background-color: #ffffff; color: #6b7280; border: 1px solid #e5e7eb;"
              @mouseover="$event.currentTarget.style.backgroundColor = '#f9fafb'"
              @mouseleave="$event.currentTarget.style.backgroundColor = '#ffffff'"
            >
              {{ directT('contacts.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Contact Modal -->
    <div v-if="showAddModal" class="add-contact-modal fixed inset-0 z-[10000]"
         :class="isMobile ? 'top-[80px]' : 'flex items-center justify-center p-4'">
      <!-- Backdrop with blur -->
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="closeAddContactModal"></div>
      
      <!-- Modal Content -->
      <div class="relative transform transition-all duration-300 scale-100 opacity-100"
           :class="isMobile ? 'w-full h-full' : 'w-full max-w-lg'">
        <div class="modal-content overflow-hidden"
             :class="isMobile ? 'h-full flex flex-col rounded-3xl bg-white dark:bg-[#1c1c1e]' : 'bg-white dark:bg-[#1c1c1e] rounded-3xl shadow-2xl'">
          <!-- Modal Header -->
          <div class="relative px-6 py-5"
               :class="isMobile ? 'rounded-t-3xl' : ''"
               style="background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);">
            <div class="flex items-center justify-between" style="align-items: center;">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <i class="material-symbols-outlined !text-[24px] text-white">
                    person_add
                  </i>
                </div>
                <h2 class="text-xl font-bold text-white" style="margin: 0;">
                  {{ directT('contacts.addNewContact') }}
                </h2>
              </div>
              <button
                @click="closeAddContactModal"
                class="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 transition-all duration-200 flex items-center justify-center group"
                style="border: none; cursor: pointer;"
              >
                <i class="material-symbols-outlined !text-[20px] text-white group-hover:rotate-90 transition-transform duration-200">
                  close
                </i>
              </button>
            </div>
      </div>

          <!-- Modal Body -->
          <div class="px-6 py-6 space-y-5 custom-scrollbar"
               :class="isMobile ? 'flex-1 overflow-y-auto' : 'max-h-[60vh] overflow-y-auto'">
            <!-- Name Field -->
            <div class="group">
              <label class="flex items-center gap-2 text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">
                <i class="material-symbols-outlined !text-[18px] text-purple-600 dark:text-purple-400">person</i>
                {{ directT('contacts.name') }}
              </label>
              <input 
                type="text" 
                v-model="newContact.name"
                :placeholder="directT('common.name')"
                class="w-full px-4 py-3 rounded-xl border-2 text-sm transition-all duration-200 focus:ring-4 focus:ring-purple-500/20 outline-none bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border-gray-200 dark:border-gray-600 focus:border-purple-500 dark:focus:border-purple-400"
              />
            </div>

            <!-- Phone Field -->
            <div class="group">
              <label class="flex items-center gap-2 text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">
                <i class="material-symbols-outlined !text-[18px] text-purple-600 dark:text-purple-400">phone</i>
                {{ directT('contacts.phone') }}
              </label>
              <input 
                type="text"
                v-model="newContact.phone"
                :placeholder="directT('common.phone')"
                class="w-full px-4 py-3 rounded-xl border-2 text-sm transition-all duration-200 focus:ring-4 focus:ring-purple-500/20 outline-none bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border-gray-200 dark:border-gray-600 focus:border-purple-500 dark:focus:border-purple-400"
              />
            </div>

            <!-- Status Field -->
            <div class="group">
              <label class="flex items-center gap-2 text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">
                <i class="material-symbols-outlined !text-[18px] text-purple-600 dark:text-purple-400">toggle_on</i>
                {{ directT('contacts.status') }}
              </label>
              <div class="relative">
                <select 
                  v-model="newContact.status"
                  class="w-full px-4 py-3 rounded-xl border-2 text-sm transition-all duration-200 focus:ring-4 focus:ring-purple-500/20 outline-none appearance-none bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border-gray-200 dark:border-gray-600 focus:border-purple-500 dark:focus:border-purple-400"
                >
                  <option value="active">{{ directT('contacts.active') }}</option>
                  <option value="deactive">{{ directT('contacts.deactive') }}</option>
                </select>
                <i class="material-symbols-outlined !text-[20px] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 dark:text-gray-500">
                  expand_more
                </i>
              </div>
            </div>

            <!-- Profile Picture URL Field -->
            <div class="group">
              <label class="flex items-center gap-2 text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">
                <i class="material-symbols-outlined !text-[18px] text-purple-600 dark:text-purple-400">image</i>
                {{ directT('contacts.profilePicture') || 'Profile Picture URL' }}
              </label>
              <input 
                type="url" 
                v-model="newContact.profile_picture_link"
                placeholder="https://example.com/image.jpg"
                class="w-full px-4 py-3 rounded-xl border-2 text-sm transition-all duration-200 focus:ring-4 focus:ring-purple-500/20 outline-none bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border-gray-200 dark:border-gray-600 focus:border-purple-500 dark:focus:border-purple-400"
      />
    </div>
  </div>

          <!-- Modal Footer -->
          <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 flex items-center justify-between gap-3">
            <button
              type="button"
              @click="closeAddContactModal"
              class="px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 hover:shadow-md"
              style="background-color: #ffffff; color: #6b7280; border: 1px solid #e5e7eb;"
              @mouseover="$event.currentTarget.style.backgroundColor = '#f9fafb'"
              @mouseleave="$event.currentTarget.style.backgroundColor = '#ffffff'"
            >
              {{ directT('contacts.cancel') }}
            </button>
            <button
              type="button"
              @click="saveContact"
              :disabled="saving"
              class="px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style="background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%); color: #ffffff;"
            >
              <span v-if="!saving">{{ directT('contacts.create') }}</span>
              <span v-else class="flex items-center gap-2">
                <div class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                {{ directT('common.saving') || 'Saving...' }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Contact Modal -->
    <div v-if="showEditModal" class="edit-contact-modal fixed inset-0 z-[10000]"
         :class="isMobile ? 'top-[80px]' : 'flex items-center justify-center p-4'">
      <!-- Backdrop with blur -->
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="closeEditModal"></div>
      
      <!-- Modal Content -->
      <div class="relative transform transition-all duration-300 scale-100 opacity-100"
           :class="isMobile ? 'w-full h-full' : 'w-full max-w-lg'">
        <div class="modal-content overflow-hidden"
             :class="isMobile ? 'h-full flex flex-col rounded-3xl bg-white dark:bg-[#1c1c1e]' : 'bg-white dark:bg-[#1c1c1e] rounded-3xl shadow-2xl'">
          <!-- Modal Header -->
          <div class="relative px-6 py-5"
               :class="isMobile ? 'rounded-t-3xl' : ''"
               style="background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);">
            <div class="flex items-center justify-between" style="align-items: center;">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <i class="material-symbols-outlined !text-[24px] text-white">
                    edit
                  </i>
                </div>
                <h2 class="text-xl font-bold text-white" style="margin: 0;">
                  {{ directT('contacts.editContact') || 'Edit Contact' }}
                </h2>
              </div>
              <button
                @click="closeEditModal"
                class="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 transition-all duration-200 flex items-center justify-center group"
                style="border: none; cursor: pointer;"
              >
                <i class="material-symbols-outlined !text-[20px] text-white group-hover:rotate-90 transition-transform duration-200">
                  close
                </i>
              </button>
            </div>
          </div>
          
          <!-- Modal Body -->
          <div class="px-6 py-6 space-y-5 custom-scrollbar"
               :class="isMobile ? 'flex-1 overflow-y-auto' : 'max-h-[60vh] overflow-y-auto'">
            <!-- Name Field -->
            <div class="group">
              <label class="flex items-center gap-2 text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">
                <i class="material-symbols-outlined !text-[18px] text-purple-600 dark:text-purple-400">person</i>
                {{ directT('contacts.name') }}
              </label>
              <input 
                type="text" 
                v-model="editingContact.name"
                :placeholder="directT('common.name')"
                class="w-full px-4 py-3 rounded-xl border-2 text-sm transition-all duration-200 focus:ring-4 focus:ring-purple-500/20 outline-none bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border-gray-200 dark:border-gray-600 focus:border-purple-500 dark:focus:border-purple-400"
              />
            </div>

            <!-- Phone Field -->
            <div class="group">
              <label class="flex items-center gap-2 text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">
                <i class="material-symbols-outlined !text-[18px] text-purple-600 dark:text-purple-400">phone</i>
                {{ directT('contacts.phone') }}
              </label>
              <input 
                type="tel" 
                v-model="editingContact.phone"
                :placeholder="directT('common.phone')"
                class="w-full px-4 py-3 rounded-xl border-2 text-sm transition-all duration-200 focus:ring-4 focus:ring-purple-500/20 outline-none bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border-gray-200 dark:border-gray-600 focus:border-purple-500 dark:focus:border-purple-400"
              />
            </div>

            <!-- Status Field -->
            <div class="group">
              <label class="flex items-center gap-2 text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">
                <i class="material-symbols-outlined !text-[18px] text-purple-600 dark:text-purple-400">toggle_on</i>
                {{ directT('contacts.status') }}
              </label>
              <div class="relative">
                <select 
                  v-model="editingContact.status"
                  class="w-full px-4 py-3 rounded-xl border-2 text-sm transition-all duration-200 focus:ring-4 focus:ring-purple-500/20 outline-none appearance-none bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border-gray-200 dark:border-gray-600 focus:border-purple-500 dark:focus:border-purple-400"
                >
                  <option value="active">{{ directT('contacts.active') }}</option>
                  <option value="deactive">{{ directT('contacts.deactive') }}</option>
                </select>
                <i class="material-symbols-outlined !text-[20px] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 dark:text-gray-500">
                  expand_more
                </i>
              </div>
            </div>

            <!-- Profile Picture URL Field -->
            <div class="group">
              <label class="flex items-center gap-2 text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">
                <i class="material-symbols-outlined !text-[18px] text-purple-600 dark:text-purple-400">image</i>
                {{ directT('contacts.profilePicture') || 'Profile Picture URL' }}
              </label>
              <input 
                type="url" 
                v-model="editingContact.profile_picture_link"
                placeholder="https://example.com/image.jpg"
                class="w-full px-4 py-3 rounded-xl border-2 text-sm transition-all duration-200 focus:ring-4 focus:ring-purple-500/20 outline-none bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border-gray-200 dark:border-gray-600 focus:border-purple-500 dark:focus:border-purple-400"
              />
            </div>
          </div>
          
          <!-- Modal Footer -->
          <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 flex items-center justify-between gap-3">
            <button
              type="button"
              @click="closeEditModal"
              class="px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 hover:shadow-md"
              style="background-color: #ffffff; color: #6b7280; border: 1px solid #e5e7eb;"
              @mouseover="$event.currentTarget.style.backgroundColor = '#f9fafb'"
              @mouseleave="$event.currentTarget.style.backgroundColor = '#ffffff'"
            >
              {{ directT('contacts.cancel') }}
            </button>
            <button
              type="button"
              @click="updateContact"
              :disabled="saving"
              class="px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style="background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); color: #ffffff;"
            >
              <span v-if="!saving">{{ directT('contacts.update') || 'Update' }}</span>
              <span v-else class="flex items-center gap-2">
                <div class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                {{ directT('common.saving') || 'Saving...' }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Contact Modal -->
    <div v-if="showDeleteModal && deletingContact" class="fixed inset-0 flex items-center justify-center z-50 p-4">
      <!-- Backdrop with blur -->
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="closeDeleteModal"></div>
      
      <!-- Modal Content -->
      <div class="relative w-full max-w-md transform transition-all duration-300 scale-100 opacity-100">
        <div class="bg-white dark:bg-[#1c1c1e] rounded-3xl shadow-2xl overflow-hidden">
          
          <!-- Modal Header -->
          <div class="px-6 py-6" style="background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <i class="material-symbols-outlined !text-[24px] text-white">
                    delete_forever
                  </i>
                </div>
                <h2 class="text-xl font-bold text-white">
                  {{ directT('contacts.deleteContact') || 'Delete Contact' }}
                </h2>
              </div>
              <button
                @click="closeDeleteModal"
                class="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 transition-all duration-200 flex items-center justify-center group border-none cursor-pointer"
              >
                <i class="material-symbols-outlined !text-[20px] text-white group-hover:rotate-90 transition-transform duration-200">
                  close
                </i>
              </button>
            </div>
          </div>
          
          <!-- Modal Body -->
          <div class="p-8">
            <!-- Warning Icon -->
            <div class="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <i class="material-symbols-outlined !text-[32px] text-red-500">
                warning
              </i>
            </div>
            
            <!-- Confirmation Message -->
            <div class="text-center mb-6">
              <p class="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {{ directT('contacts.deleteConfirmation') || 'Are you sure you want to delete this contact?' }}
              </p>
              <p class="text-base text-gray-700 dark:text-gray-300 mb-2">
                {{ deletingContact.name }}
              </p>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                {{ directT('contacts.deleteWarning') || 'This action cannot be undone' }}
              </p>
            </div>
          </div>
          
          <!-- Modal Footer -->
          <div class="px-6 py-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-600 flex justify-between gap-3">
            <button 
              @click="closeDeleteModal"
              class="px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              {{ directT('contacts.cancel') || 'Cancel' }}
            </button>
            <button 
              @click="confirmDelete"
              :disabled="deleteLoading"
              class="px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style="background-color: #ef4444; color: #ffffff;"
              @mouseover="$event.currentTarget.style.backgroundColor = '#dc2626'"
              @mouseleave="$event.currentTarget.style.backgroundColor = '#ef4444'"
            >
              <span v-if="deleteLoading" class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
              <span v-else class="flex items-center gap-2">
                <i class="material-symbols-outlined !text-[18px]">delete</i>
                {{ directT('contacts.delete') || 'Delete' }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, computed, onMounted, reactive } from "vue";
import { useI18n } from "vue-i18n";
import stateStore from "@/utils/store";
import { supabase } from "@/lib/supabaseClient";
import { profileService } from "@/utils/supabase";
// ViewContactPopup component would be imported here if needed

// Define translation record type
type TranslationRecord = {
  [key: string]: string | TranslationRecord;
};

// Define Contact type
type Contact = {
  id: string | number;
  name: string;
  phone?: string | null;
  status: 'active' | 'deactive';
  profile_picture_link?: string | null;
  last_contact?: string | null;
  summery?: string | null;
  created_at?: string;
  organization_id?: number;
  [key: string]: any; // Allow additional properties
};

export default {
  name: "ContactsPage",
  components: {
    // ViewContactPopup would be added here if needed
  },
  setup() {
    const stateStoreInstance = stateStore;
    const contacts = ref<Contact[]>([]);
    const loading = ref<boolean>(true);
    const errorMessage = ref<string | null>(null);
    const showViewPopup = ref<boolean>(false);
    const showAddModal = ref(false);
    const showEditModal = ref(false);
    const selectedContact = ref<Contact | null>(null);
    const searchQuery = ref<string>('');
    const saving = ref(false);
    const organizationId = ref<number | null>(null);
    
    // Delete modal states
    const showDeleteModal = ref(false);
    const deletingContact = ref<Contact | null>(null);
    const deleteLoading = ref(false);
    
    // Pagination
    const currentPage = ref<number>(1);
    const itemsPerPage = ref<number>(10);
    const totalContacts = ref<number>(0);
    
    // i18n setup
    const { locale, t } = useI18n();
    
    // New contact form
    const newContact = ref({
      name: '',
      email: '',
      phone: '',
      company: '',
      status: 'active' as 'active' | 'deactive',
      profile_picture_link: ''
    });
    
    // Edit contact form
    const editingContact = ref({
      id: '',
      name: '',
      phone: '',
      status: 'active' as 'active' | 'deactive',
      profile_picture_link: ''
    });
    
    // Direct translation function
    const directT = (key: string): string => {
      return t(key);
    };
    
    // Computed
    const filteredContacts = computed(() => {
      if (!searchQuery.value) return contacts.value;
      
      const query = searchQuery.value.toLowerCase();
      return contacts.value.filter(contact => 
        contact.name?.toLowerCase().includes(query) ||
        contact.phone?.toLowerCase().includes(query)
      );
    });
    
    const totalContactsComputed = computed(() => filteredContacts.value.length);
    
    // Statistics computed properties
    const newContactsCount = computed(() => {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      return contacts.value.filter(contact => {
        if (!contact.created_at) return false;
        const createdDate = new Date(contact.created_at);
        return createdDate >= sevenDaysAgo;
      }).length;
    });
    
    const activeContactsCount = computed(() => {
      return contacts.value.filter(contact => contact.status === 'active').length;
    });
    
    const inactiveContactsCount = computed(() => {
      return contacts.value.filter(contact => contact.status === 'deactive').length;
    });
    
    // Get organization ID
    const fetchOrganizationId = async () => {
      try {
        const { data: profile, error } = await profileService.getCurrentProfile();
        if (error) throw error;
        organizationId.value = (profile?.org_id as number) || null;
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    // Methods
    const fetchContacts = async () => {
      loading.value = true;
      errorMessage.value = null;
      
      try {
        if (!organizationId.value) {
          console.error('No organization ID found');
          loading.value = false;
          return;
        }
        
        // First get the total count for pagination
        const { count, error: countError } = await supabase
          .from('contacts')
          .select('*', { count: 'exact' })
          .eq('organization_id', organizationId.value);
        
        if (countError) throw countError;
        totalContacts.value = count || 0;
        
        // Then get the actual contacts for the current page
        const from = (currentPage.value - 1) * itemsPerPage.value;
        const to = from + itemsPerPage.value - 1;
        
        const { data, error } = await supabase
          .from('contacts')
          .select('*')
          .eq('organization_id', organizationId.value)
          .range(from, to);
        
        if (error) throw error;
        contacts.value = (data || []) as Contact[];
        
      } catch (error) {
        console.error('Error fetching contacts:', error);
        errorMessage.value = directT('contacts.errorLoadingContacts') || 'Error loading contacts';
      } finally {
        loading.value = false;
      }
    };
    
    const openViewPopup = (contact: Contact) => {
      selectedContact.value = contact;
      showViewPopup.value = true;
    };
    
    const closeViewPopup = () => {
      showViewPopup.value = false;
      selectedContact.value = null;
    };
    
    const openAddContactModal = () => {
      showAddModal.value = true;
    };
    
    const closeAddContactModal = () => {
      showAddModal.value = false;
      newContact.value = {
        name: '',
        email: '',
        phone: '',
        company: '',
        status: 'active' as 'active' | 'deactive',
        profile_picture_link: ''
      };
    };
    
    const openEditModal = (contact: Contact) => {
      editingContact.value = {
        id: String(contact.id),
        name: contact.name,
        phone: contact.phone || '',
        status: contact.status,
        profile_picture_link: contact.profile_picture_link || ''
      };
      showEditModal.value = true;
    };
    
    const closeEditModal = () => {
      showEditModal.value = false;
      editingContact.value = {
        id: '',
        name: '',
        phone: '',
        status: 'active' as 'active' | 'deactive',
        profile_picture_link: ''
      };
    };
    
    const saveContact = async () => {
      if (!newContact.value.name) {
        alert(directT('contacts.nameRequired'));
        return;
      }

      saving.value = true;
      try {
        const { error } = await supabase
          .from('contacts')
          .insert({
            name: newContact.value.name,
            phone: newContact.value.phone || null,
            status: newContact.value.status,
            last_contact: null,
            profile_picture_link: newContact.value.profile_picture_link || null,
            summery: null,
            organization_id: organizationId.value
          });
        
        if (error) {
          throw error;
        }
        
        alert(directT('contacts.contactCreatedSuccessfully'));
        closeAddContactModal();
        fetchContacts();
      } catch (error) {
        console.error('Error creating contact:', error);
        alert(directT('contacts.failedToCreateContact'));
      } finally {
        saving.value = false;
      }
    };
    
    const updateContact = async () => {
      if (!editingContact.value.name) {
        alert(directT('contacts.nameRequired'));
        return;
      }

      saving.value = true;
      try {
        const { error } = await supabase
          .from('contacts')
          .update({
            name: editingContact.value.name,
            phone: editingContact.value.phone || null,
            status: editingContact.value.status,
            profile_picture_link: editingContact.value.profile_picture_link || null
          })
          .eq('id', editingContact.value.id);
        
        if (error) {
          throw error;
        }
        
        alert(directT('contacts.contactUpdatedSuccessfully'));
        closeEditModal();
        fetchContacts();
      } catch (error) {
        console.error('Error updating contact:', error);
        alert(directT('contacts.failedToUpdateContact'));
      } finally {
        saving.value = false;
      }
    };
    
    const deleteContact = async (contact: Contact) => {
      deletingContact.value = contact;
      showDeleteModal.value = true;
    };
    
    const closeDeleteModal = () => {
      showDeleteModal.value = false;
      deletingContact.value = null;
    };
    
    const confirmDelete = async () => {
      if (!deletingContact.value) return;

      try {
        deleteLoading.value = true;
        const { error } = await supabase
          .from('contacts')
          .delete()
          .eq('id', deletingContact.value.id);
        
        if (error) {
          throw error;
        }

        // Update local list after successful deletion
        contacts.value = contacts.value.filter(c => c.id !== deletingContact.value!.id);
        closeDeleteModal();
      } catch (error) {
        console.error('Error deleting contact:', error);
        alert(directT('contacts.deleteError') || 'Failed to delete contact');
      } finally {
        deleteLoading.value = false;
      }
    };

    const getAvatarColor = (name: string) => {
      const colors = [
        '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', 
        '#10b981', '#06b6d4', '#6366f1', '#f43f5e'
      ];
      const index = name ? name.charCodeAt(0) % colors.length : 0;
      return colors[index];
    };

    const getInitials = (name: string) => {
      if (!name) return '?';
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return parts[0].charAt(0).toUpperCase() + parts[1].charAt(0).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    };

    const getStatusStyle = (status: string) => {
      if (status === 'active') {
        return {
          'background-color': '#d1fae5',
          'color': '#065f46'
        };
      }
      return {
        'background-color': '#f3f4f6',
        'color': '#6b7280'
      };
    };

    const handlePageChange = (page: number) => {
      currentPage.value = page;
      fetchContacts();
    };

    const formatDate = (dateString: string | null | undefined) => {
      if (!dateString) return directT('common.notAvailable');
      
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString(locale.value, {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch {
        return directT('common.notAvailable');
      }
    };

    // Check RTL
    const isRTL = computed(() => {
      return locale.value === 'he' || locale.value === 'ar';
    });

    // Dark mode detection
    const isDarkMode = computed(() => {
      return document.documentElement && document.documentElement.classList.contains('dark');
    });

    // Mobile detection
    const isMobile = computed(() => {
      return window.innerWidth <= 768;
    });

    // Lifecycle
    onMounted(async () => {
      await fetchOrganizationId();
      await fetchContacts();
    });

    return {
      directT,
      contacts,
      loading,
      errorMessage,
      showViewPopup,
      showAddModal,
      showEditModal,
      showDeleteModal,
      selectedContact,
      deletingContact,
      searchQuery,
      currentPage,
      itemsPerPage,
      totalContacts: totalContactsComputed,
      newContact,
      editingContact,
      saving,
      deleteLoading,
      filteredContacts,
      newContactsCount,
      activeContactsCount,
      inactiveContactsCount,
      openViewPopup,
      closeViewPopup,
      openAddContactModal,
      closeAddContactModal,
      openEditModal,
      closeEditModal,
      saveContact,
      updateContact,
      deleteContact,
      closeDeleteModal,
      confirmDelete,
      getAvatarColor,
      getInitials,
      getStatusStyle,
      handlePageChange,
      formatDate,
      isDarkMode,
      isRTL,
      isMobile
    };
  },
};
</script>

<style scoped>
/* Custom Animations */
@keyframes fade-in-down {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade-in-left {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fade-in-right {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes bounce-in {
  0% {
    transform: scale(0) rotate(180deg);
    opacity: 0;
  }
  50% {
    transform: scale(1.2) rotate(90deg);
    opacity: 0.8;
  }
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
}

.animate-fade-in-down {
  animation: fade-in-down 0.8s ease-out forwards;
}

.animate-fade-in-up {
  animation: fade-in-up 0.8s ease-out forwards;
  opacity: 0;
}

.animate-fade-in-left {
  animation: fade-in-left 0.8s ease-out forwards;
  opacity: 0;
}

.animate-fade-in-right {
  animation: fade-in-right 0.8s ease-out forwards;
  opacity: 0;
}

.animate-bounce-in {
  animation: bounce-in 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
  opacity: 0;
}

/* Custom scrollbar for better UX */
.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  @apply bg-gray-300 dark:bg-gray-700 rounded;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  @apply bg-gray-400 dark:bg-gray-600;
}

/* Animation for loading spinner */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Dark mode adjustments for the table */
:root.dark table {
  @apply bg-[#15203c];
}

:root.dark table thead tr {
  @apply bg-[#1a2332];
}

:root.dark table tbody tr {
  @apply border-gray-800;
}

:root.dark table th,
:root.dark table td {
  @apply text-gray-300;
}

:root.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  @apply bg-gray-700;
}

:root.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  @apply bg-gray-600;
}

/* Desktop Table Column Spacing */
@media (min-width: 769px) {
  .mobile-table {
    table-layout: fixed;
  }
  
  /* All columns - 25% each */
  .mobile-table th,
  .mobile-table td {
    width: 25%;
  }
}

/* Mobile Table Scroll Styles */
@media (max-width: 768px) {
  .mobile-table-scroll {
    /* Enable smooth horizontal scrolling on mobile */
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
  }
  
  .mobile-table {
    /* Set minimum width to ensure table doesn't compress too much */
    min-width: 500px;
    table-layout: auto !important;
  }
  
  .mobile-table th,
  .mobile-table td {
    /* Prevent text wrapping in table cells on mobile */
    white-space: nowrap;
  }
  
  /* Name column - wider for profile info */
  .mobile-table th:nth-child(1),
  .mobile-table td:nth-child(1) {
    min-width: 180px;
  }
  
  /* Phone column */
  .mobile-table th:nth-child(2),
  .mobile-table td:nth-child(2) {
    min-width: 140px;
  }
  
  /* Status column */
  .mobile-table th:nth-child(3),
  .mobile-table td:nth-child(3) {
    min-width: 100px;
  }
  
  /* Action column */
  .mobile-table th:nth-child(4),
  .mobile-table td:nth-child(4) {
    min-width: 80px;
  }
}

/* Enhanced scrollbar for mobile */
@media (max-width: 768px) {
  .mobile-table-scroll::-webkit-scrollbar {
    height: 6px;
  }
  
  .mobile-table-scroll::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }
  
  .mobile-table-scroll::-webkit-scrollbar-thumb {
    background: rgba(59, 130, 246, 0.6);
    border-radius: 3px;
  }
  
  .mobile-table-scroll::-webkit-scrollbar-thumb:hover {
    background: rgba(59, 130, 246, 0.8);
  }
}

/* View Contact Popup Styles */
.view-contact-popup {
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
}
.view-contact-popup.active {
  opacity: 1;
  visibility: visible;
}

.view-contact-popup .popup-dialog > div {
  transform: scale(0.9) translateY(20px);
  transition: all 0.3s ease;
}

.view-contact-popup.active .popup-dialog > div {
  transform: scale(1) translateY(0);
}

/* Override transform for mobile */
@media (max-width: 768px) {
  .view-contact-popup .popup-dialog > div,
  .view-contact-popup.active .popup-dialog > div {
    transform: none !important;
  }
}

/* Responsive Add Contact Modal */
@media (max-width: 768px) {
  .add-contact-modal {
    padding: 16px !important;
    align-items: flex-end !important;
  }
  
  .add-contact-modal > div:last-child {
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }
  
  .add-contact-modal .modal-content {
    border-radius: 24px !important;
    max-height: 85vh !important;
    overflow-y: auto !important;
    margin-bottom: 16px !important;
  }
  
  /* Header adjustments */
  .add-contact-modal .relative.px-6.py-5 {
    padding: 16px 20px !important;
  }
  
  /* Body adjustments */
  .add-contact-modal .px-6.py-6.space-y-5 {
    padding: 20px !important;
    max-height: none !important;
  }
  
  /* Footer adjustments */
  .add-contact-modal .px-6.py-4.border-t {
    padding: 16px 20px !important;
    position: sticky !important;
    bottom: 0 !important;
    background: inherit !important;
  }
  
  /* Input and button adjustments */
  .add-contact-modal input,
  .add-contact-modal select {
    height: 48px !important;
    font-size: 16px !important; /* Prevents zoom on iOS */
  }
  
  .add-contact-modal button {
    min-height: 44px !important;
    font-size: 16px !important;
  }
}

/* Responsive View Contact Modal */
@media (max-width: 768px) {
  .view-contact-modal {
    padding: 16px !important;
    padding-top: 80px !important; /* Space for top navigation */
  }
  
  .view-contact-modal .popup-dialog {
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    min-height: auto !important;
    align-items: flex-start !important;
    justify-content: flex-start !important;
  }
  
  .view-contact-modal .modal-content {
    border-radius: 24px !important;
    max-height: calc(100vh - 96px) !important; /* Full height minus top padding and bottom margin */
    overflow-y: auto !important;
    margin-top: 0 !important;
    transform: none !important; /* Override existing transform */
  }
  
  /* Header adjustments */
  .view-contact-modal .relative.px-6.py-5 {
    padding: 16px 20px !important;
  }
  
  /* Body adjustments */
  .view-contact-modal .p-6.space-y-6 {
    padding: 20px !important;
    max-height: none !important;
  }
  
  /* Footer adjustments */
  .view-contact-modal .px-6.py-4.border-t {
    padding: 16px 20px !important;
    position: sticky !important;
    bottom: 0 !important;
    background: inherit !important;
  }
  
  .view-contact-modal button {
    min-height: 44px !important;
    font-size: 16px !important;
  }
}

/* Responsive Edit Contact Modal */
@media (max-width: 768px) {
  .edit-contact-modal {
    padding: 16px !important;
    align-items: flex-end !important;
  }
  
  .edit-contact-modal > div:last-child {
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }
  
  .edit-contact-modal .modal-content {
    border-radius: 24px !important;
    max-height: 85vh !important;
    overflow-y: auto !important;
    margin-bottom: 16px !important;
  }
  
  /* Header adjustments */
  .edit-contact-modal .relative.px-6.py-5 {
    padding: 16px 20px !important;
  }
  
  /* Body adjustments */
  .edit-contact-modal .px-6.py-6.space-y-5 {
    padding: 20px !important;
    max-height: none !important;
  }
  
  /* Footer adjustments */
  .edit-contact-modal .px-6.py-4.border-t {
    padding: 16px 20px !important;
    position: sticky !important;
    bottom: 0 !important;
    background: inherit !important;
  }
  
  /* Input and button adjustments */
  .edit-contact-modal input,
  .edit-contact-modal select {
    height: 48px !important;
    font-size: 16px !important; /* Prevents zoom on iOS */
  }
  
  .edit-contact-modal button {
    min-height: 44px !important;
    font-size: 16px !important;
  }
}
</style>
